import { useEffect, useState } from 'react';

import useApolloClient from 'shared/hooks/use-apollo-client';
import useUI from 'shared/hooks/use-ui';
import { chainDispensaryNamesAndIds } from 'shared/graphql/dispensary/queries';

import { ChainDispensary } from './chain-dispensary-select.types';

type UseChainDispensariesOptions = {
  /** Override the chainId to fetch. If not provided, uses UI.dispensary.chain */
  chainId?: string;
  /** Whether to sort the active dispensary to the top of the list */
  sortActiveToTop?: boolean;
};

type UseChainDispensariesReturn = {
  chainDispensaries: ChainDispensary[] | null;
  loading: boolean;
  error: Error | null;
};

/**
 * Hook to fetch chain dispensaries data.
 * Extracts the data fetching logic from chain-user-management-setting.tsx for reuse.
 */
export function useChainDispensaries(options: UseChainDispensariesOptions = {}): UseChainDispensariesReturn {
  const { chainId: overrideChainId, sortActiveToTop = true } = options;
  const apolloClient = useApolloClient();
  const UI = useUI();

  const [chainDispensaries, setChainDispensaries] = useState<ChainDispensary[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const chainId = overrideChainId ?? UI.dispensary.chain;
  const activeDispensaryId = UI.dispensary.id;

  useEffect(() => {
    const queryChainDispensaries = async (): Promise<void> => {
      setLoading(true);
      setError(null);

      // If no chain and no active dispensary, nothing to fetch
      if (!chainId && !activeDispensaryId) {
        setChainDispensaries([]);
        setLoading(false);
        return;
      }

      // If no chain but we have an active dispensary, return just that dispensary
      if (!chainId) {
        setChainDispensaries([
          {
            id: activeDispensaryId ?? '',
            name: UI.dispensary.name ?? '',
            address: UI.dispensary.address ?? '',
            state: UI.dispensary.location?.state ?? '',
          },
        ]);
        setLoading(false);
        return;
      }

      try {
        const { data } = await apolloClient.query({
          fetchPolicy: 'network-only',
          query: chainDispensaryNamesAndIds,
          variables: { chainId },
        });

        if (data.chainDispensaryNamesAndIds) {
          // Map dispensaries to include state at top level
          let dispensaries: ChainDispensary[] = data.chainDispensaryNamesAndIds.map(
            (d: { id: string; name: string; address: string; location?: { state?: string } }) => ({
              id: d.id,
              name: d.name,
              address: d.address,
              state: d.location?.state ?? '',
            })
          );

          // Sort active dispensary to top if requested
          if (sortActiveToTop && activeDispensaryId) {
            const activeDispensary = dispensaries.find((el: ChainDispensary) => el.id === activeDispensaryId);
            if (activeDispensary) {
              const filteredChains = dispensaries.filter((el: ChainDispensary) => el.id !== activeDispensaryId);
              dispensaries = [{ ...activeDispensary }, ...filteredChains];
            }
          }

          setChainDispensaries(dispensaries);
        } else {
          setChainDispensaries([]);
        }
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch chain dispensaries'));
        setChainDispensaries([]);
      } finally {
        setLoading(false);
      }
    };

    void queryChainDispensaries();
  }, [
    apolloClient,
    chainId,
    activeDispensaryId,
    sortActiveToTop,
    UI.dispensary.name,
    UI.dispensary.address,
    UI.dispensary.location?.state,
  ]);

  return {
    chainDispensaries,
    loading,
    error,
  };
}
